<script lang="ts">
	import type { DeepPartial } from "@apollo/client/utilities";
	import CartProduct from "@components/CartProduct.svelte";
	import CartSteps from "@components/CartSteps.svelte";
	import CartSummary from "@components/CartSummary.svelte";
	import CrossSale from "@components/CrossSale.svelte";
	import FreeDeliveryThreshold from "@components/FreeDeliveryThreshold.svelte";
	import Button from "@components/common/Button.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import Preloader from "@components/common/Preloader.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import SavedForm from "@components/form/SavedForm.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { inViewport } from "@utils/inViewport.js";
	import { initSentry } from "@utils/initSentry.js";
	import { onMount } from "svelte";
	import type { EventHandler } from "svelte/elements";
	import { writable } from "svelte/store";
	import basket from "@core/assets/icons/basket.svg?raw";
	import flame from "@core/assets/icons/flame.svg";
	import { CantPayOnlineReason } from "@core/schema/CantPayOnlineReason.js";
	import { Locale } from "@core/schema/Locale.js";
	import { campfireLimit } from "@core/schema/campfireLimit.js";
	import { maxOnlinePrice } from "@core/schema/maxOnlinePrice.js";
	import { getPriceOfOrder } from "@core/schema/utils/getPriceOfOrder.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte";
	import { reportCheckoutBegan } from "../UniversalAnalytics.js";
	import type { CartFieldsForStep } from "../cart/Cart.js";
	import { CartStep, getCartStepPath, getNextStep } from "../cart/CartStep.js";
	import { getCorrectCartInStepOrRedirect } from "../cart/getCorrectCartInStepOrRedirect.js";
	import { trySaveCartFields } from "../cart/trySaveCartFields.js";
	import { getCartStore } from "../contexts/cartContextKey.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import type { CartOverviewProps } from "./CartOverviewProps.js";

	const { tenant, products }: CartOverviewProps = $props();
	const [campfire, ...crossSaleProducts] = $derived(products);
	const cartStore = getCartStore();

	const globalSettings = getGlobalSettings();

	const currentStep = CartStep.Overview;
	const nextStep = getNextStep(currentStep);
	type FieldsForStep = CartFieldsForStep<typeof currentStep>;
	const { cartInStep } = $derived(
		getCorrectCartInStepOrRedirect(Locale.cs, currentStep, $state.snapshot($cartStore.value)),
	);
	const price = $derived(getPriceOfOrder(cartInStep.order));

	onMount(async () => {
		await initSentry(globalSettings);
		reportCheckoutBegan(cartInStep.order);
	});

	const advance = createPromiseWithStatus(
		(input: FieldsForStep): Promise<boolean> => cartStore.advanceToNextStep(currentStep, input),
	);

	function validateFields(fields: DeepPartial<FieldsForStep>): fields is FieldsForStep {
		return [].every(Boolean);
	}

	function getFields(form: HTMLFormElement, validate: true): FieldsForStep;
	function getFields(form: HTMLFormElement, validate: false): DeepPartial<FieldsForStep>;
	function getFields(form: HTMLFormElement, validate: boolean): FieldsForStep | DeepPartial<FieldsForStep> {
		const fields: DeepPartial<FieldsForStep> = {};
		if (validate && !validateFields(fields)) {
			throw new Error("Fields are not valid");
		}
		return fields;
	}

	async function saveFields(form: HTMLFormElement): Promise<boolean> {
		return await trySaveCartFields(cartStore, form, currentStep, cartInStep, getFields(form, false));
	}

	const onsubmit: EventHandler<SubmitEvent, HTMLFormElement> = async (e): Promise<void> => {
		e.preventDefault();
		const fields = getFields(e.currentTarget, true);
		const canAdvance = await advance.call(fields);
		if (!canAdvance) {
			return;
		}

		window.location.assign(getCartStepPath(nextStep, tenant.locale));
	};
	const hasItemsInCart = $derived(Boolean($cartStore.value.productsInOrder.length));
	const continueInViewport = writable(false);
	const prices = $derived(getPriceOfOrder($cartStore.value.order));
</script>

<Layout hideFooter>
	<SavedForm {saveFields} {onsubmit} class="flex flex-col gap-2 pb-16">
		<h1 class="h1 !text-xl">Obsah košíku</h1>

		{#if hasItemsInCart}
			<CartSteps {currentStep} {nextStep} />
		{/if}

		{#if !$cartStore.loaded}
			<Preloader />
		{:else}
			<div class="flex flex-col 2xl:flex-row gap-12">
				<div class="flex flex-col grow">
					{#if hasItemsInCart}
						{#each cartInStep.order.products as orderProduct, index (orderProduct.id)}
							<CartProduct
								{orderProduct}
								{index}
								maxQuantity={orderProduct.productId === campfire?.id ? campfireLimit : undefined}
								{products}
							/>
						{/each}
						<div class="flex flex-col items-end py-2">
							<div
								use:inViewport={{
									store: continueInViewport,
								}}
								class="text-right"
							>
								<p class="text-md lg:text-base">K úhradě s DPH</p>
								<p class="text-lg leading-tight lg:text-md sm:text-right font-bold lg:text-lg">
									{formatPrice(price.totalPrice.withTax, tenant.locale, true)}
								</p>
							</div>

							{#if $cartStore.value.properties.cantPayOnlineReasons.includes(CantPayOnlineReason.PriceTooHigh)}
								<p class="text-xs text-nov-red-200">
									Maximální cena objednávky je {formatPrice(maxOnlinePrice, tenant.locale, true)}
								</p>
							{/if}
						</div>
					{:else}
						<div class="mx-auto mb-24 mt-16 flex flex-col gap-2 font-medium col-span-6">
							<Icon icon={basket} class="text-secondary mx-auto mb-4 size-24" />
							<p class="text-md text-center md:text-lg">Košík je prázdný</p>
						</div>
					{/if}
				</div>
				{#if hasItemsInCart}
					<div class="lg:min-w-132 flex flex-col justify-end lg:items-end">
						<div>
							<FreeDeliveryThreshold {prices} />
						</div>
						<Button
							variant={ButtonVariant.Primary}
							class="mt-6"
							type="submit"
							disabled={advance.value.status === "loading"}
						>
							Pokračovat v objednávce
						</Button>
					</div>
				{/if}
			</div>
			<CartSummary isButtonDisabled={advance.value.status === "loading"} hide={$continueInViewport} />
		{/if}
	</SavedForm>
	{#if hasItemsInCart}
		<div class="mx-auto pb-32 max-w-full">
			<div class="text-lg leading-tight mt-4 mb-6 font-bold text-center flex items-center justify-center">
				<span>Doporučujeme dokoupit</span>
				<Media
					alt="🔥"
					width={32}
					height={32}
					source={{ src: flame, type: "image/svg+xml" }}
					class="size-[1.25em] -translate-y-[0.125em] hidden md:block"
				/>
			</div>
			<CrossSale {crossSaleProducts} />
		</div>
	{/if}
</Layout>
